import {
  ENDOSCOPY_KIND_COLONOSCOPY,
  ENDOSCOPY_KIND_EGDS,
  ENDOSCOPY_PROTOCOL_TEMPLATES,
  getEndoscopyProtocolTemplate as getBuiltinEndoscopyTemplate,
  resolveEndoscopyKind,
} from 'constants/endoscopyProtocolTemplates';

export const ENDOSCOPY_PROTOCOL_FORM_TYPE = 'endoscopy_protocol';

/** @typedef {{ id?: number|string, name?: string, diseaseLabel?: string, payload?: object }} SavedEndoscopyTemplate */

export function endoscopyTemplatePayload(template) {
  const payload = template?.payload || {};
  return payload.endoscopyProtocol || payload.endoscopy_protocol || payload;
}

export function normalizeSavedEndoscopyTemplate(raw) {
  const section = endoscopyTemplatePayload(raw);
  return {
    id: raw?.id,
    name: String(raw?.name || '').trim(),
    diseaseLabel: String(raw?.diseaseLabel || raw?.disease_label || '').trim(),
    title: String(section.title || '').trim(),
    serviceMatch: String(section.serviceMatch || section.service_match || '').trim(),
    equipment: String(section.equipment || '').trim(),
    department: String(section.department || '').trim(),
    anesthesia: String(section.anesthesia || '').trim(),
    findings: String(section.findings || section.protocolSpecialtyStatus || '').trim(),
    conclusion: String(section.conclusion || section.diagnosis || '').trim(),
    kind: String(section.kind || '').trim(),
  };
}

export function savedEndoscopyTemplateToBuiltinShape(template) {
  const n = normalizeSavedEndoscopyTemplate(template);
  const serviceName = n.serviceMatch || n.name || '';
  const kind =
    n.kind === ENDOSCOPY_KIND_COLONOSCOPY || n.kind === ENDOSCOPY_KIND_EGDS
      ? n.kind
      : resolveEndoscopyKind(serviceName || n.title);

  return {
    id: n.id,
    name: n.name,
    title: n.title || n.name || getBuiltinEndoscopyTemplate(serviceName).title,
    findings: n.findings,
    conclusion: n.conclusion,
    equipment: n.equipment,
    department: n.department,
    anesthesia: n.anesthesia,
    serviceMatch: n.serviceMatch,
    kind,
  };
}

function scoreTemplateMatch(template, serviceName = '') {
  const n = normalizeSavedEndoscopyTemplate(template);
  const name = String(serviceName || '').toLowerCase();
  if (!name) return 0;

  const keywords = n.serviceMatch
    .split(/[,;\n]+/)
    .map((part) => part.trim().toLowerCase())
    .filter(Boolean);

  let score = 0;
  keywords.forEach((keyword) => {
    if (keyword && name.includes(keyword)) score += 10 + keyword.length;
  });

  if (n.diseaseLabel && name.includes(n.diseaseLabel.toLowerCase())) score += 5;
  if (n.name && name.includes(n.name.toLowerCase())) score += 3;
  if (n.title && name.includes(n.title.toLowerCase())) score += 2;

  const kind = resolveEndoscopyKind(name);
  const templateKind = n.kind || resolveEndoscopyKind(n.serviceMatch || n.title);
  if (kind && templateKind && kind === templateKind) score += 1;

  return score;
}

/** Лучший пользовательский шаблон по названию услуги или null. */
export function resolveSavedEndoscopyTemplate(templates, serviceName = '') {
  const list = Array.isArray(templates) ? templates : [];
  if (!list.length) return null;

  let best = null;
  let bestScore = 0;
  list.forEach((template) => {
    const score = scoreTemplateMatch(template, serviceName);
    if (score > bestScore) {
      best = template;
      bestScore = score;
    }
  });

  return bestScore > 0 ? savedEndoscopyTemplateToBuiltinShape(best) : null;
}

/** Встроенный или пользовательский шаблон для услуги. */
export function resolveEndoscopyTemplate(serviceName = '', savedTemplates = []) {
  const custom = resolveSavedEndoscopyTemplate(savedTemplates, serviceName);
  if (custom) return custom;
  return getBuiltinEndoscopyTemplate(serviceName);
}

export function applySavedEndoscopyTemplateToDraft(draft, templateShape) {
  if (!templateShape) return draft;
  const conclusion = String(templateShape.conclusion || '').trim();
  const prevMeta = draft?.protocolMeta && typeof draft.protocolMeta === 'object' ? draft.protocolMeta : {};
  return {
    ...draft,
    protocolSpecialty: 'endoscopy',
    protocolSpecialtyStatus: templateShape.findings || draft.protocolSpecialtyStatus || '',
    protocolMeta: {
      title: templateShape.title || prevMeta.title || '',
      equipment: templateShape.equipment || prevMeta.equipment || '',
      caseNumber: prevMeta.caseNumber || '',
      department: templateShape.department || prevMeta.department || '',
      anesthesia: templateShape.anesthesia || prevMeta.anesthesia || '',
    },
    diagnosis: conclusion || draft.diagnosis || '',
    diagnoses: conclusion
      ? [{ id: `endoscopy-${templateShape.id || 'tpl'}`, code: '', type: 'clinical', text: conclusion }]
      : draft.diagnoses,
  };
}

export function buildEndoscopyTemplatePayloadFromDraft(draft, meta = {}) {
  const protocolMeta = draft?.protocolMeta || {};
  const conclusion = String(draft?.diagnoses?.[0]?.text || draft?.diagnosis || '').trim();
  return {
    endoscopyProtocol: {
      title: String(meta.title || '').trim(),
      serviceMatch: String(meta.serviceMatch || '').trim(),
      kind: String(meta.kind || '').trim(),
      equipment: String(protocolMeta.equipment || meta.equipment || '').trim(),
      department: String(protocolMeta.department || meta.department || '').trim(),
      anesthesia: String(protocolMeta.anesthesia || meta.anesthesia || '').trim(),
      findings: String(draft?.protocolSpecialtyStatus || meta.findings || '').trim(),
      conclusion,
    },
  };
}
